import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { PlusIcon, PencilSquareIcon, TrashIcon, XMarkIcon, UsersIcon, ExclamationTriangleIcon, LockClosedIcon, ArrowPathIcon, CheckCircleIcon, XCircleIcon, CheckIcon, StarIcon } from '@heroicons/react/24/outline';

interface SuperAdminUser {
  id: string;
  email: string;
  display_name?: string;
  name?: string;
  role: 'owner' | 'admin' | 'viewer' | 'developer';
  is_active: boolean;
  can_manage_users: boolean;
  can_modify_settings: boolean;
  can_access_database_tools: boolean;
  mfa_enabled: boolean;
  last_login?: string;
  created_at: string;
  failed_login_attempts: number;
  locked_until?: string;
  // Multi-admin enhancement fields
  company_id?: string;
  is_company_owner?: boolean;
  onboarding_completed_for_company?: boolean;
  onboarding_completed_at?: string;
}

interface UserActivity {
  activity_type: string;
  page_path?: string;
  api_endpoint?: string;
  created_at: string;
}

interface Company {
  id: string;
  company_name: string;
  company_code: string;
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<SuperAdminUser[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedUser, setSelectedUser] = useState<SuperAdminUser | null>(null);
  const [userActivity, setUserActivity] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [modalTab, setModalTab] = useState<'profile' | 'company' | 'permissions'>('profile');
  const [formData, setFormData] = useState<{ email: string; display_name: string; name?: string; role: SuperAdminUser['role']; can_manage_users: boolean; can_modify_settings: boolean; can_access_database_tools: boolean; company_id?: string; is_company_owner?: boolean; }>({
    email: '',
    display_name: '',
    name: '',
    role: 'viewer',
    can_manage_users: false,
    can_modify_settings: false,
    can_access_database_tools: false,
    company_id: '',
    is_company_owner: false,
  });

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  const fetchUsers = async () => {
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      const response = await api.get('/super-admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      const response = await api.get('/super-admin/companies');
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchUserActivity = async (userId: string) => {
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      const response = await api.get(`/super-admin/users/${userId}/activity`);
      setUserActivity(response.data.activity || []);
    } catch (error) {
      console.error('Error fetching user activity:', error);
    }
  };

  const openCreateModal = () => {
    setFormData({
      email: '',
      display_name: '',
      name: '',
      role: 'viewer',
      can_manage_users: false,
      can_modify_settings: false,
      can_access_database_tools: false,
    });
    setIsEditing(false);
    setModalTab('profile');
    setShowModal(true);
  };

  const openEditModal = (user: SuperAdminUser) => {
    setFormData({
      email: user.email,
      display_name: user.display_name || '',
      role: user.role,
      can_manage_users: user.can_manage_users,
      can_modify_settings: user.can_modify_settings,
      can_access_database_tools: user.can_access_database_tools,
    });
    setSelectedUser(user);
    setIsEditing(true);
    setModalTab('profile');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      if (isEditing && selectedUser) {
        await api.put(`/super-admin/users/${selectedUser.id}`, formData);
      } else {
        await api.post('/super-admin/users', formData);
      }
      
      setShowModal(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
      alert(error.response?.data?.message || 'Failed to save user');
    }
  };

  const toggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      await api.put(`/super-admin/users/${userId}`, { is_active: !currentStatus });
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const deleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      await api.delete(`/super-admin/users/${userId}`);
      fetchUsers();
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const resetFailedAttempts = async (userId: string) => {
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      await api.post(`/super-admin/users/${userId}/reset-attempts`, {});
      fetchUsers();
    } catch (error) {
      console.error('Error resetting failed attempts:', error);
    }
  };

  const viewUserDetails = (user: SuperAdminUser) => {
    setSelectedUser(user);
    fetchUserActivity(user.id);
  };

  const getRoleBadgeColor = (role: string): string => {
    switch (role) {
      case 'owner': return 'bg-violet-100 text-violet-700';
      case 'admin': return 'bg-blue-100 text-blue-700';
      case 'developer': return 'bg-emerald-100 text-emerald-700';
      case 'viewer': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UsersIcon className="w-7 h-7 text-blue-600" />
            User Management
          </h1>
          <button onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors">
            <PlusIcon className="w-4 h-4" />
            Add User
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - User List */}
          <div className="lg:col-span-1 space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 text-center">
                <div className="text-xs text-slate-500 mb-0.5">Total</div>
                <div className="text-xl font-bold text-slate-900">{users.length}</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 text-center">
                <div className="text-xs text-slate-500 mb-0.5">Active</div>
                <div className="text-xl font-bold text-emerald-600">{users.filter(u => u.is_active).length}</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 text-center">
                <div className="text-xs text-slate-500 mb-0.5">MFA</div>
                <div className="text-xl font-bold text-violet-600">{users.filter(u => u.mfa_enabled).length}</div>
              </div>
            </div>

            {/* User List */}
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id}
                  className={`bg-white rounded-xl border shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow ${selectedUser?.id === u.id ? 'border-blue-400 ring-1 ring-blue-100' : 'border-slate-200'}`}
                  onClick={() => viewUserDetails(u)}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-semibold text-slate-900 truncate" title={u.display_name || u.email}>{u.display_name || u.email}</span>
                      {!u.is_active && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">Inactive</span>}
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${getRoleBadgeColor(u.role)}`}>{u.role}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate" title={u.email}>{u.email}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                    {u.mfa_enabled && <span className="flex items-center gap-1"><LockClosedIcon className="w-3 h-3" /> MFA</span>}
                    {u.last_login && <span>Last login: {new Date(u.last_login).toLocaleDateString()}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Details */}
          <div className="lg:col-span-2">
            {selectedUser ? (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                {/* Detail Header */}
                <div className="flex items-start justify-between p-6 border-b border-slate-200">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{selectedUser.display_name || selectedUser.email}</h2>
                    <p className="text-sm text-slate-500">{selectedUser.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEditModal(selectedUser)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                      <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => toggleUserStatus(selectedUser.id, selectedUser.is_active)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${selectedUser.is_active ? 'text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100' : 'text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100'}`}>
                      {selectedUser.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    {user?.email !== selectedUser.email && (
                      <button onClick={() => deleteUser(selectedUser.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                        <TrashIcon className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Account Info */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Account Information</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <span className="block text-xs text-slate-500 mb-0.5">Role</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${getRoleBadgeColor(selectedUser.role)}`}>{selectedUser.role}</span>
                      </div>
                      <div>
                        <span className="block text-xs text-slate-500 mb-0.5">Status</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${selectedUser.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{selectedUser.is_active ? 'Active' : 'Inactive'}</span>
                      </div>
                      <div>
                        <span className="block text-xs text-slate-500 mb-0.5">MFA</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${selectedUser.mfa_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                          {selectedUser.mfa_enabled ? <CheckCircleIcon className="w-3.5 h-3.5" /> : <XCircleIcon className="w-3.5 h-3.5" />}
                          {selectedUser.mfa_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div>
                        <span className="block text-xs text-slate-500 mb-0.5">Created</span>
                        <span className="text-sm text-slate-700">{new Date(selectedUser.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Company Assignment */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Company Assignment</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="block text-xs text-slate-500 mb-0.5">Company</span>
                        <span className="text-sm text-slate-700">
                          {selectedUser.company_id 
                            ? companies.find(c => c.id === selectedUser.company_id)?.company_name || selectedUser.company_id
                            : 'Global Admin (All Companies)'}
                        </span>
                      </div>
                      {selectedUser.is_company_owner && (
                        <div>
                          <span className="block text-xs text-slate-500 mb-0.5">Role</span>
                          <span className="inline-flex items-center gap-1 text-sm text-amber-600 font-medium">
                            <StarIcon className="w-4 h-4" /> Company Owner
                          </span>
                        </div>
                      )}
                      {selectedUser.onboarding_completed_for_company && (
                        <div>
                          <span className="block text-xs text-slate-500 mb-0.5">Onboarding</span>
                          <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
                            <CheckCircleIcon className="w-4 h-4" />
                            Completed {selectedUser.onboarding_completed_at
                              ? `on ${new Date(selectedUser.onboarding_completed_at).toLocaleDateString()}`
                              : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Permissions */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Permissions</h3>
                    <div className="space-y-2">
                      {[
                        { label: 'Manage Users', granted: selectedUser.can_manage_users },
                        { label: 'Modify Settings', granted: selectedUser.can_modify_settings },
                        { label: 'Access Database Tools', granted: selectedUser.can_access_database_tools },
                      ].map(perm => (
                        <div key={perm.label} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50">
                          <span className="text-sm text-slate-700">{perm.label}</span>
                          {perm.granted
                            ? <CheckIcon className="w-4 h-4 text-emerald-600" />
                            : <XMarkIcon className="w-4 h-4 text-slate-400" />}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Security Alerts */}
                  {selectedUser.failed_login_attempts > 0 && (
                    <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 space-y-2">
                      <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2"><ExclamationTriangleIcon className="w-4 h-4" /> Security Alerts</h3>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-amber-700">Failed login attempts: {selectedUser.failed_login_attempts}</span>
                        <button onClick={() => resetFailedAttempts(selectedUser.id)}
                          className="px-3 py-1 text-xs font-medium text-amber-700 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors">
                          <ArrowPathIcon className="w-3 h-3 inline mr-1" />Reset
                        </button>
                      </div>
                      {selectedUser.locked_until && (
                        <p className="inline-flex items-center gap-1 text-sm text-amber-700">
                          <LockClosedIcon className="w-3.5 h-3.5" />
                          Locked until: {new Date(selectedUser.locked_until).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Recent Activity */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Recent Activity</h3>
                    {userActivity.length === 0 ? (
                      <p className="text-sm text-slate-400 py-4 text-center">No recent activity</p>
                    ) : (
                      <div className="space-y-1">
                        {userActivity.slice(0, 10).map((activity, index) => (
                          <div key={index} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 text-sm">
                            <span className="font-medium text-slate-700">{activity.activity_type}</span>
                            <span className="text-slate-500 truncate mx-4 max-w-[200px]">{activity.page_path || activity.api_endpoint || 'N/A'}</span>
                            <span className="text-xs text-slate-400 shrink-0">{new Date(activity.created_at).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex items-center justify-center py-24">
                <div className="text-center">
                  <UsersIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Select a user to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto p-4" onClick={() => setShowModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mt-16 mb-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">{isEditing ? 'Edit User' : 'Add New User'}</h2>
                <button onClick={() => setShowModal(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              
              {/* Modal tabs */}
              <div className="flex items-center gap-1 px-6 pt-3 border-b border-slate-200">
                {([
                  { key: 'profile', label: 'Profile' },
                  { key: 'company', label: 'Company' },
                  { key: 'permissions', label: 'Permissions' },
                ] as const).map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setModalTab(t.key)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      modalTab === t.key
                        ? 'border-blue-600 text-blue-700'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {modalTab === 'profile' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                      <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required disabled={isEditing} placeholder="user@example.com"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow disabled:opacity-50 disabled:bg-slate-50" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name</label>
                      <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Display Name</label>
                      <input type="text" value={formData.display_name} onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                        placeholder="John"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Role</label>
                      <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value as any })} required
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                        <option value="viewer">Viewer - Read-only access</option>
                        <option value="developer">Developer - Full access to development tools</option>
                        <option value="admin">Admin - Can manage settings and users</option>
                        <option value="owner">Owner - Full system control</option>
                      </select>
                    </div>
                  </>
                )}

                {modalTab === 'company' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Assignment *</label>
                      <select value={formData.company_id} onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                        required={!isEditing} disabled={isEditing}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50 disabled:bg-slate-50">
                        <option value="">{isEditing ? 'Select company to change...' : 'Select a company'}</option>
                        {companies.map(company => (
                          <option key={company.id} value={company.id}>{company.company_name} ({company.company_code})</option>
                        ))}
                      </select>
                      {!isEditing && <p className="text-xs text-slate-400 mt-1">Required: Each super admin must be assigned to a company</p>}
                    </div>
                    {!isEditing && (
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" checked={formData.is_company_owner} onChange={(e) => setFormData({ ...formData, is_company_owner: e.target.checked })}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                        Set as company owner (primary admin)
                      </label>
                    )}
                  </>
                )}

                {modalTab === 'permissions' && (
                  <div className="space-y-2">
                    {[
                      { key: 'can_manage_users' as const, label: 'Can manage users' },
                      { key: 'can_modify_settings' as const, label: 'Can modify settings' },
                      { key: 'can_access_database_tools' as const, label: 'Can access database tools (CRITICAL - read-only queries)' },
                    ].map(perm => (
                      <label key={perm.key} className="flex items-start gap-2 text-sm text-slate-700 py-1.5">
                        <input type="checkbox" checked={formData[perm.key]} onChange={(e) => setFormData({ ...formData, [perm.key]: e.target.checked })}
                          className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                        {perm.label}
                      </label>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                  <button type="button" onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Cancel</button>
                  <button type="submit"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors">
                    {isEditing ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
